import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Mail, Lock, User, Building2 } from "lucide-react";
import { Logo, Footer } from "@/components/Chrome";
import { useAuth, startLogin } from "@/context/AuthContext";
import { api } from "@/lib/spott";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const errText = (detail) => {
  if (detail == null) return "Algo salió mal. Intenta de nuevo.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  if (detail?.msg) return detail.msg;
  return String(detail);
};

const Field = ({ label, icon: Icon, ...props }) => (
  <div className="space-y-2">
    <Label className="text-sm font-medium text-spott-blue">{label}</Label>
    <div className="flex items-center gap-2 rounded-lg border border-spott-mid bg-white px-3">
      <Icon size={15} className="shrink-0 text-spott-yellow" />
      <Input {...props} className="border-0 px-0 focus-visible:ring-0" />
    </div>
  </div>
);

export default function Login() {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "", company_name: "" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "forgot") {
        const { data } = await api.post("/auth/forgot-password", { email: form.email });
        toast.success(data.message);
        setMode("login");
        return;
      }
      const { data } = mode === "login"
        ? await api.post("/auth/login", { email: form.email, password: form.password })
        : await api.post("/auth/register", form);
      setUser(data);
      toast.success(mode === "login" ? `Bienvenido, ${data.name.split(" ")[0]}` : "Cuenta creada");
      navigate(data.role === "admin" || data.role === "agente" ? "/admin" : "/portal");
    } catch (err) {
      toast.error(errText(err?.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-spott-light">
      <div className="relative overflow-hidden bg-spott-blue py-6 grain">
        <div className="mx-auto max-w-7xl px-5 lg:px-10"><Logo reverse /></div>
      </div>

      <div className="mx-auto max-w-md px-5 py-14" data-testid="login-page">
        <h1 className="text-2xl font-bold tracking-tight text-spott-blue sm:text-3xl">
          {mode === "login" ? "Ingresa a tu cuenta"
            : mode === "register" ? "Crea tu cuenta Spott" : "Recuperar contraseña"}
        </h1>
        <p className="mt-2 text-sm text-gray-600">
          {mode === "login"
            ? "Accede a tus cotizaciones, campañas y puntos Spott."
            : mode === "register"
              ? "Al crearla verás campañas y reportes de demostración para explorar la plataforma."
              : "Ingresa tu correo y te enviamos un enlace para crear una contraseña nueva."}
        </p>

        <form onSubmit={submit} className="mt-8 space-y-5 rounded-xl border border-spott-mid bg-white p-6">
          {mode === "register" && (
            <>
              <Field label="Nombre y apellido" icon={User} value={form.name} data-testid="login-name"
                onChange={(e) => setForm({ ...form, name: e.target.value })} required />
              <Field label="Empresa / Marca" icon={Building2} value={form.company_name} data-testid="login-company"
                onChange={(e) => setForm({ ...form, company_name: e.target.value })} />
            </>
          )}
          <Field label="Correo electrónico" icon={Mail} type="email" value={form.email} data-testid="login-email"
            onChange={(e) => setForm({ ...form, email: e.target.value })} required autoComplete="email" />
          {mode !== "forgot" && (
            <Field label="Contraseña" icon={Lock} type="password" value={form.password} data-testid="login-password"
              onChange={(e) => setForm({ ...form, password: e.target.value })} required
              autoComplete={mode === "login" ? "current-password" : "new-password"}
              placeholder={mode === "register" ? "Mínimo 8 caracteres" : ""} />
          )}
          {mode === "login" && (
            <button type="button" onClick={() => setMode("forgot")} data-testid="login-forgot-link"
              className="text-xs font-semibold text-spott-blue underline">
              ¿Olvidaste tu contraseña?
            </button>
          )}
          <button type="submit" disabled={busy} data-testid="login-submit"
            className="w-full rounded-full bg-spott-yellow py-3.5 text-sm font-bold text-spott-blue transition-transform hover:scale-[1.02] disabled:opacity-60">
            {busy ? "Procesando…"
              : mode === "login" ? "Ingresar"
              : mode === "register" ? "Crear cuenta" : "Enviar enlace"}
          </button>
          {mode === "forgot" && (
            <button type="button" onClick={() => setMode("login")} data-testid="login-back-to-login"
              className="w-full text-center text-xs font-semibold text-spott-blue underline">
              Volver a iniciar sesión
            </button>
          )}
        </form>

        <div className="mt-6 flex items-center gap-3 text-xs text-gray-400">
          <span className="h-px flex-1 bg-spott-mid" /> o continúa con <span className="h-px flex-1 bg-spott-mid" />
        </div>
        <button onClick={() => startLogin("/portal")} data-testid="login-google-btn"
          className="mt-4 w-full rounded-full border border-spott-mid bg-white py-3.5 text-sm font-bold text-spott-blue transition-colors hover:border-spott-yellow">
          Continuar con Google
        </button>

        <p className="mt-8 text-center text-sm text-gray-600">
          {mode === "register" ? "¿Ya tienes cuenta?" : "¿Aún no tienes cuenta?"}{" "}
          <button onClick={() => setMode(mode === "register" ? "login" : "register")} data-testid="login-toggle-mode"
            className="font-semibold text-spott-blue underline">
            {mode === "register" ? "Ingresar" : "Crear una"}
          </button>
        </p>
        <p className="mt-6 text-center text-xs text-gray-500">
          <Link to="/" className="underline">Volver al inicio</Link>
        </p>
      </div>
      <Footer />
    </div>
  );
}
